import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  Typography,
  Divider,
  Box,
  Stack,
  Chip,
  RadioGroup,
  FormControlLabel,
  Radio,
  LinearProgress,
  FormControl,
  TextField,
  Checkbox,
  Container,
  Tooltip,
  FormGroup,
  Paper,
  Avatar,
  Badge,
} from '@mui/material';
import API from '../../api/api';
import { setSnackError } from '../../Reducers/snackErrors';
import { useDispatch, useSelector } from 'react-redux';
import { Cloud, RemoveCircle, CheckCircle, RadioButtonUnchecked } from '@mui/icons-material';
import { setSuccessSnack } from '../../Reducers/snackSuccess';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';
import SessionLinkerPanel from '../DialogActions/SessionLinkPanel';


// ─── LaTeX renderer ──────────────────────────────────────────────────────────
/**
 * Renders a string that may contain inline ($…$) or block ($$…$$) LaTeX,
 * falling back to plain text when no math markers are detected.
 * Applied consistently to BOTH question text AND choice labels.
 */
function renderMathText(text) {
  if (!text) return null;
  if (!text.includes('$')) return <span>{text}</span>;

  // Split on block math ($$…$$) or inline math ($…$), preserving delimiters
  const regex = /(\$\$[\s\S]+?\$\$|\$[^$\n]+?\$)/g;
  const parts = text.split(regex);

  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('$$') && part.endsWith('$$')) {
          return (
            <Box key={i} sx={{ my: 1, overflowX: 'auto' }}>
              <BlockMath math={part.slice(2, -2)} />
            </Box>
          );
        }
        if (part.startsWith('$') && part.endsWith('$')) {
          return <InlineMath key={i} math={part.slice(1, -1)} />;
        }
        return part.length > 0 ? <span key={i}>{part}</span> : null;
      })}
    </>
  );
}


// ─── Choice label with LaTeX ──────────────────────────────────────────────────
function ChoiceLabel({ letter, text, correct, proctor }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        py: 0.25,
      }}
    >
      <Avatar
        sx={{
          width: 24,
          height: 24,
          fontSize: '0.7rem',
          fontWeight: 700,
          flexShrink: 0,
          mt: '2px',
          bgcolor: !proctor && correct ? 'success.main' : 'grey.200',
          color: !proctor && correct ? 'white' : 'text.secondary',
        }}
      >
        {letter}
      </Avatar>
      <Typography
        variant="body2"
        component="span"
        sx={{
          color: !proctor && correct ? 'success.dark' : 'text.primary',
          fontWeight: !proctor && correct ? 600 : 400,
          lineHeight: 1.6,
          '& .katex': { fontSize: '1em' },
        }}
      >
        {renderMathText(text)}
      </Typography>
    </Box>
  );
}


// ─── Question type badge ──────────────────────────────────────────────────────
const TYPE_LABELS = {
  multiple_choice: { label: 'Multiple Choice', color: 'primary' },
  true_false: { label: 'True / False', color: 'secondary' },
  multi_select_choice: { label: 'Multi-Select', color: 'info' },
  short_answer: { label: 'Short Answer', color: 'warning' },
};


// ─── Main component ───────────────────────────────────────────────────────────
const AssessmentQuestions = ({
  open,
  onClose,
  assessmentId,
  proctor = false,
  studentId = null,
  grader = null,
  sessionToken = null,
  semesterId= null,
  sessionId=null
}) => {
  const role = useSelector((state) => state.role);
  const [questions, setQuestions] = useState([]);
  const [previousSession, setPreviousSession] = useState(false);
  const dispatch = useDispatch();
  const [checkedAnswers, setCheckedAnswers] = useState({});
  const [answers, setAnswers] = useState({});
  const [savedAnswers, setSavedAnswers] = useState({});
  const [autoGrader, setAutoGrader] = useState(false);
  const [answeredAt, setAnsweredAt] = useState(null);


  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleChoiceSelect = (questionId, choiceId) => {
    setAnswers(prev => ({ ...prev, [questionId]: choiceId }));
  };

  const handleMultiSelect = (questionId, choiceId, checked) => {
    if (checked) {
      setAnswers(prev => ({
        ...prev,
        [questionId]: prev[questionId]
          ? [...prev[questionId], choiceId]
          : [choiceId],
      }));
    } else {
      setAnswers(prev => ({
        ...prev,
        [questionId]: (prev[questionId] || []).filter(id => id !== choiceId),
      }));
    }
  };

  const handleShortAnswer = (questionId, checked) => {
    setCheckedAnswers(prev => ({ ...prev, [questionId]: checked }));
  };


  // ── Session persistence ──────────────────────────────────────────────────────
  const saveAssessmentSession = async (session_id) => {
    if (!studentId || !sessionToken || !session_id) return;
    try {
      const data = {
        session_id: session_id,
        student_id: Number(studentId),
        assessment_id: Number(assessmentId),
        tutor_id: Number(role.id),
        answers,
        session_token: sessionToken,
        grader: checkedAnswers,
        auto_grade: autoGrader,
        student_completed_at: answeredAt,
      }
      const response = await API.post("/create_assessment_answers", data);
      if (response.data.message_id){
        dispatch(setSuccessSnack({ title: 'Success!', body: 'Autograder will grade this assessment.' }));
        return
      }
      if (response.data.data) {
        dispatch(setSuccessSnack({ title: 'Success!', body: 'Thank you. We have logged your assessment.' }));
        return
      }
    } catch (error) {
      console.error(error);
      dispatch(setSnackError({ title: 'Error', body: 'Unable to create assessment response' }));
      return
    }
    finally {
      onClose();
    }
  };

  const deleteStudentAssessmentOnline = async (student_id) => {
    try {
      const token = sessionToken
      if (!token) return;
      const response = await API.post('/delete_student_session', {
        session_id: token,
        student_id: student_id,
      });
      if (response.data.status === 'Deleted') {
        dispatch(setSuccessSnack({ title: 'Success', body: 'Removed student assessment.' }));
      }
    } catch (error) {
      if (error.isAuthError || error.isForbidden) return;
      dispatch(
        setSnackError({
          title: error.isNetworkError ? 'Connection Error' : 'Error found',
          body: error.message || 'Unable to delete session.',
        }),
      );
    }
    finally{
      onClose();
    }
  };



  // ── Data fetching ────────────────────────────────────────────────────────────
  const groupQuestions = flatArray => {
    const grouped = {};
    flatArray.forEach(item => {
      const qid = item.question_id;
      if (!grouped[qid]) {
        grouped[qid] = {
          question_id: item.question_id,
          assessment_id: item.assessment_id,
          question_text: item.question_text,
          question_type: item.question_type,
          image_url: item.image_url,
          points: item.points,
          order_number: item.order_number,
          choices: [],
        };
      }
      if (item.choice_id) {
        grouped[qid].choices.push({
          choice_id: item.choice_id,
          choice_text: item.choice_text,
          is_correct: item.is_correct,
          order_number: item.choice_order,
        });
      }
    });
    return Object.values(grouped);
  };

  const getAssessments = async () => {
    try {
      const response = await API.get('/get_assessment_questions', {
        params: { assessment_id: assessmentId },
      });
      if (response.data.data) setQuestions(groupQuestions(response.data.data));
    } catch (error) {
      if (error.isAuthError || error.isForbidden) return;
      dispatch(
        setSnackError({
          title: error.isNetworkError ? 'Connection Error' : 'Error found',
          body: error.message || 'Unable to get assessment questions.',
        }),
      );
    }
  };

  // -- Get assessment choices if answerd
  const getAssessmentChoices = async () => {
    try {
      if (!proctor) return;

      const response = await API.get('/get_student_assessment_choices', {
        params: { assessment_id: assessmentId, student_id: studentId, session_id: sessionToken ?? sessionId },
      });

      const answerData = response?.data?.data;
      if (!answerData) { return; }
      
      const grouped = {};
      for (const { question_id, choice_id, answer_text, answered_at } of answerData) {
        if (!choice_id && !answer_text) continue;
        if (!(question_id in grouped)) {
          grouped[question_id] = choice_id || answer_text;
        } else {
          const existing = grouped[question_id];
          if (Array.isArray(existing)) {
            if (choice_id) existing.push(choice_id);
          } else {
            grouped[question_id] = choice_id ? [existing, choice_id] : existing;
          }
        }
        if (answered_at && !answeredAt) {
          setAnsweredAt(answered_at)
        }
      }
      setSavedAnswers(grouped);
      setAnswers(grouped);
    } catch (error) {
      if (error.isAuthError || error.isForbidden) return;
      dispatch(
        setSnackError({
          title: error.isNetworkError ? 'Connection Error' : 'Error found',
          body: error.message || 'Unable to load choices.',
        }),
      );
    }
  };

  useEffect(() => {
    if (open && assessmentId) {
      getAssessments();
      getAssessmentChoices();
    }
    return () => {
      setQuestions([]);
      setAnswers({});
      setSavedAnswers({});
      setCheckedAnswers({});
      setPreviousSession(false);
      setAnsweredAt(null);
    };
  }, [open, assessmentId]);


  // ── Layout helpers ───────────────────────────────────────────────────────────
  const fullScreen = proctor;
  const answeredCount = Object.keys(answers).length;
  const progress = questions.length > 0 ? (answeredCount / questions.length) * 100 : 0;


  // ── Question card renderer ───────────────────────────────────────────────────
  const renderChoices = question => {
    const { question_id, question_type, choices } = question;

    // ── Non-proctor: read-only chips/text ──────────────────────────────────────
    if (!proctor) {
      if (question_type === 'short_answer') {
        return choices?.map(c => (
          <TextField
            key={c.choice_id}
            disabled
            value={c.choice_text}
            size="small"
            rows={4}
            multiline
            fullWidth
            sx={{ mt: 1, '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'text.primary' } }}
          />
        ));
      }
      return (
        <Stack spacing={0.75} sx={{ mt: 1.5 }}>
          {choices.map((c, i) => (
            <Box
              key={c.choice_id}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                px: 1.5,
                py: 1,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: c.is_correct ? 'success.light' : 'divider',
                bgcolor: c.is_correct ? 'success.50' : 'transparent',
                transition: 'background 0.2s',
              }}
            >
              {c.is_correct
                ? <CheckCircle fontSize="small" sx={{ color: 'success.main', mt: '2px', flexShrink: 0 }} />
                : <RadioButtonUnchecked fontSize="small" sx={{ color: 'text.disabled', mt: '2px', flexShrink: 0 }} />
              }
              <Typography
                variant="body2"
                sx={{
                  color: c.is_correct ? 'success.dark' : 'text.primary',
                  fontWeight: c.is_correct ? 600 : 400,
                  '& .katex': { fontSize: '1em' },
                }}
              >
                <strong>{String.fromCharCode(65 + i)}.</strong>{' '}
                {renderMathText(c.choice_text)}
              </Typography>
            </Box>
          ))}
        </Stack>
      );
    }

    // ── Proctor: interactive ───────────────────────────────────────────────────
    if (question_type === 'multiple_choice' || question_type === 'true_false') {
      return (
        <RadioGroup
          value={answers[question_id] ?? ''}
          onChange={e => handleChoiceSelect(question_id, parseInt(e.target.value))}
          sx={{ mt: 1 }}
        >
          {choices.map((c, i) => {
            const selected = answers[question_id] === c.choice_id;
            return (
              <Box
                key={c.choice_id}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1.5,
                  mb: 0.5,
                  border: '1px solid',
                  borderColor: selected ? 'success.main' : 'divider',
                  bgcolor: selected ? 'secondary.50' : 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <FormControlLabel
                  value={c.choice_id}
                  control={<Radio color="success" size="small" />}
                  label={
                    <Typography variant="body2" sx={{ '& .katex': { fontSize: '1em' } }}>
                      <strong>{String.fromCharCode(65 + i)}.</strong>{' '}
                      {renderMathText(c.choice_text)}
                    </Typography>
                  }
                  sx={{ m: 0, width: '100%' }}
                />
              </Box>
            );
          })}
        </RadioGroup>
      );
    }

    if (question_type === 'multi_select_choice') {
      return (
        <FormGroup sx={{ mt: 1 }}>
          {choices?.map((c, i) => {
            const questionAnswers = answers[question_id];
            const isChecked = Array.isArray(questionAnswers)
              ? questionAnswers.includes(c.choice_id)
              : false;
            return (
              <Box
                key={c.choice_id}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1.5,
                  mb: 0.5,
                  border: '1px solid',
                  borderColor: isChecked ? 'success.main' : 'divider',
                  bgcolor: isChecked ? 'success.50' : 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      color="success"
                      size="small"
                      checked={isChecked}
                      onChange={e =>
                        handleMultiSelect(question_id, c.choice_id, e.target.checked)
                      }
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ '& .katex': { fontSize: '1em' } }}>
                      <strong>{String.fromCharCode(65 + i)}.</strong>{' '}
                      {renderMathText(c.choice_text)}
                    </Typography>
                  }
                  sx={{ m: 0, width: '100%' }}
                />
              </Box>
            );
          })}
        </FormGroup>
      );
    }

    if (question_type === 'short_answer') {
      const isSaved = !!savedAnswers[question_id];
      return (
        <FormControl fullWidth sx={{ mt: 1 }}>
          <TextField
            color="success"
            value={
              savedAnswers[question_id]?.answer_text ??
              answers[question_id] ??
              ''
            }
            onChange={e =>
              !isSaved && handleChoiceSelect(question_id, e.target.value)
            }
            rows={3}
            multiline
            fullWidth
            focused={isSaved}
            disabled={isSaved}
            placeholder="Type student's answer here…"
            size="small"
          />
          {grader && (
            <FormControlLabel
              label={
                <Typography variant="body2" color={checkedAnswers[question_id] ? 'success.main' : 'text.secondary'}>
                  Mark as correct
                </Typography>
              }
              control={
                <Checkbox
                  color="success"
                  checked={checkedAnswers[question_id] || false}
                  onChange={e => handleShortAnswer(question_id, e.target.checked)}
                />
              }
              sx={{ mt: 0.5 }}
            />
          )}
        </FormControl>
      );
    }
    return null;
  };


  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <Dialog
      fullScreen={fullScreen}
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          overflow: 'hidden',
        },
      }}
    >
      {/* ── Header ── */}
      <DialogTitle
        sx={{
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            Assessment Questions
          </Typography>
          {proctor && (
            <Box>
              <Tooltip title="Remove this student's saved assessment">
              <Button
                onClick={() => deleteStudentAssessmentOnline(studentId)}
                variant="outlined"
                color="error"
                size="small"
                startIcon={<RemoveCircle />}
              >
                Delete
              </Button>
            </Tooltip>
            {answeredAt ? <Typography>{answeredAt}</Typography>: null}
            </Box>
          )}
        </Box>

        {/* Progress bar – proctor only */}
        {proctor && (
          <Box sx={{ mt: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {answeredCount} / {questions.length} answered
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {Math.round(progress)}%
              </Typography>
            </Box>
            <LinearProgress
              color="success"
              variant="determinate"
              value={progress}
              sx={{ height: 6, borderRadius: 3 }}
            />
            <Typography variant="body2" color="primary.main" sx={{ mt: 0.5, display: 'block' }}>
              ⚠ Saving here overrides any online submission.
            </Typography>
          </Box>
        )}
      </DialogTitle>

      {/* ── Content ── */}
      <DialogContent sx={{ p: 0, bgcolor: 'grey.50' }}>
        <Container maxWidth="md" sx={{ py: 2 }}>
          <Stack spacing={2}>
            {questions.map((question, idx) => {
              const typeInfo = TYPE_LABELS[question.question_type] || { label: question.question_type, color: 'default' };
              const isAnswered = question.question_id in answers;
              return (
                <Paper
                  key={question.question_id}
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 3 },
                    border: '1px solid',
                    borderColor: isAnswered && proctor ? 'primary.light' : 'divider',
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    transition: 'border-color 0.2s',
                  }}
                >
                  {/* Question header */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5, gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                      {/* Question number badge */}
                      <Avatar
                        sx={{
                          width: 30,
                          height: 30,
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          flexShrink: 0,
                          bgcolor: isAnswered && proctor ? 'primary.main' : 'grey.300',
                          color: isAnswered && proctor ? 'white' : 'text.secondary',
                          mt: '2px',
                        }}
                      >
                        {idx + 1}
                      </Avatar>

                      {/* Question text with LaTeX */}
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        sx={{
                          lineHeight: 1.7,
                          '& .katex-display': { my: 0.5 },
                          '& .katex': { fontSize: '1.05em' },
                        }}
                      >
                        {renderMathText(question.question_text)}
                      </Typography>
                    </Box>

                    {/* Points badge */}
                    <Chip
                      label={`${question.points} pt${question.points !== 1 ? 's' : ''}`}
                      size="small"
                      variant="outlined"
                      color="default"
                      sx={{ flexShrink: 0, fontWeight: 600 }}
                    />
                  </Box>

                  {/* Type label */}
                  <Chip
                    label={typeInfo.label}
                    size="small"
                    color={typeInfo.color}
                    variant="soft"
                    sx={{ mb: 1.5, fontSize: '0.7rem', height: 20 }}
                  />

                  {/* Question image */}
                  {question.image_url && question.image_url !== 'NA' && question.image_url !== '' && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mb: 2,
                        borderRadius: 1.5,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <img
                        src={question.image_url}
                        alt="Question illustration"
                        style={{
                          maxWidth: '100%',
                          maxHeight: 320,
                          objectFit: 'contain',
                          display: 'block',
                        }}
                      />
                    </Box>
                  )}

                  {/* Choices / answer area */}
                  {renderChoices(question)}
                </Paper>
              );
            })}

            {questions.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                <Typography variant="body1">Loading questions…</Typography>
              </Box>
            )}
          </Stack>
        </Container>
      </DialogContent>

      {/* ── Footer ── */}
      <SessionLinkerPanel
        proctor={proctor}
        onClose={onClose}
        studentId={studentId}
        previousSession={previousSession}
        assessmentId={assessmentId}
        semesterID={semesterId}
        answers={answers}
        setAutoGrader={setAutoGrader}
        autoGrader={autoGrader}
        onFinalSubmit={async (linkedSession) => {
          await saveAssessmentSession(linkedSession.session_id);
        }}
      />
    </Dialog>
  );
};

export default AssessmentQuestions;